import React from 'react'
import _ from 'lodash'
import axios from 'axios'
import moment from 'moment-timezone'
import 'moment/locale/en-gb'
import {
  ContentSectionBlock,
  Col,
  Row,
  FormInput,
  FormInputSelect,
  FormInputDateRange,
  Card,
  Spinner,
  Button
} from 'camino-react'

import { csrfToken } from 'Utilities/Csrf'
import ReportAccordion from 'Components/Reports/Tickets/ReportAccordion'
import { humanize } from 'Utilities/StringManipulator'

export default class TicketsReport extends React.Component {
  constructor (props) {
    super(props)
    const { activeFilters, tickets } = props
    const {
      status_id: statusProp,
      type_group: ticketTypeProp,
      date_status_id: ticketDateProp,
      start_date: dateFromProp,
      end_date: dateToProp,
      ticket_queue_id: queueProp
    } = activeFilters

    const start = moment(new Date())
    const dateFrom = moment(start).subtract(1, 'months').seconds(0)
    const dateTo = moment(start).seconds(0)

    this.defaultFilters = {
      status: statusProp || 'open_on_hold',
      ticket_type: ticketTypeProp || 'All',
      ticket_date: ticketDateProp || 'created_at',
      group: 'Assignee',
      date_from: dateFromProp ? new Date(dateFromProp) : new Date(dateFrom),
      date_to: dateToProp ? new Date(dateToProp) : new Date(dateTo),
      queue_id: queueProp || 'all'
    }

    this.state = {
      tickets,
      loadingTickets: false,
      filters: this.defaultFilters,
      assignees: [],
      clients: [],
      contacts: []
    }
  }

  fetchTickets = () => {
    const { endPoint } = this.props
    const { filters } = this.state
    this.setState({ loadingTickets: true })

    axios.patch(endPoint, {
      authenticity_token: csrfToken(),
      report: { ...filters }
    })
      .then(({ data: { tickets } }) => this.applyNewTickets(tickets))
  }

  applyNewTickets = (tickets) => {
    if (tickets) { this.setState({ tickets }) }

    return Promise.all([
    ])
      .then(() => {
        this.buildTicketData()
      })
      .then(() => {
        this.setState({ loadingTickets: false })
      })
  }

  buildTicketData = () => {
    this.setState((prevState) => {
      const {
        tickets,
        contacts
      } = prevState

      const newTickets = _.map(tickets, (ticket) => {
        const contact = _.find(contacts, ({ id }) => (ticket.client_contact_id === id))

        return Object.assign({},
          ticket,
          contact && { contact_name: contact.name }
        )
      })

      return { tickets: newTickets }
    })
  }

  resetFilters = () => {
    this.setState({ filters: this.defaultFilters }, this.fetchTickets)
  }

  handleFilterInputChange = (filterName, value) => {
    const updateFilters = (state, props) => {
      const { filters } = state
      const newFilters = Object.assign({}, filters, { [filterName]: value })

      return (
        { filters: newFilters }
      )
    }

    this.setState(updateFilters)
  }

  handleDateRangeInputChange = (dates) => {
    if (dates[0] && dates[1]) {
      this.handleFilterInputChange('date_from', dates[0])
      this.handleFilterInputChange('date_to', dates[1])
    }
  }

  handleOnClickApplyFilters = () => {
    this.setState(this.fetchTickets)
  }

  render () {
    const { statuses, ticketTypes, queues } = this.props
    const {
      resetFilters,
      handleFilterInputChange,
      handleDateRangeInputChange,
      handleOnClickApplyFilters
    } = this
    const {
      filters,
      tickets,
      loadingTickets
    } = this.state
    const {
      group,
      date_from: dateFrom,
      date_to: dateTo
    } = filters

    const extraStatuses = [
      { value: 'open_on_hold', label: 'Open | On hold' },
      { value: 'all', label: 'All' }
    ]

    const extraTicketTypes = [
      { value: 'All', label: 'All' }
    ]

    const groupLabelDecorator = group => _.upperCase(group.match(/SLA/)) + _.replace(humanize(_.kebabCase(group)), 'Sla', ' ')

    const ticketDatePivots = ticketDates => _.map(ticketDates, dateType => (
      { value: dateType, label: humanize(dateType) }
    ))

    const groupPivots = groups => _.map(groups, ({ name }) => (
      { value: name, label: groupLabelDecorator(name) }
    ))

    const defaultGroups = [
      {
        name: 'Assignee',
        ticketField: 'assignee_name',
        labels: {
          undefined: '(No assignee)'
        }
      },
      {
        name: 'Client',
        ticketField: 'client_name'
      },
      {
        name: 'Category',
        ticketField: 'service_category_name'
      },
      {
        name: 'Priority',
        ticketField: 'priority_name'
      },
      {
        name: 'SLAStatus',
        ticketField: 'sla_status'
      },
      {
        name: 'Chargeable',
        ticketField: 'chargeable',
        labels: {
          true: 'Chargeable',
          false: 'Not chargeable'
        }
      },
      {
        name: 'TicketType',
        ticketField: 'ticket_type_name'
      },
      {
        name: 'Queue',
        ticketField: 'queue_name'
      }
    ]

    const selectInputFilters = [
      {
        title: 'Status',
        field: 'status',
        options: [...extraStatuses, ...statuses]
      },
      {
        title: 'Ticket Type',
        field: 'ticket_type',
        options: [...extraTicketTypes, ...ticketTypes]
      },
      {
        title: 'Ticket Date',
        field: 'ticket_date',
        options: ticketDatePivots(['created_at', 'updated_at', 'on_hold_at', 'resolved_at'])
      },
      {
        title: 'Group',
        field: 'group',
        options: groupPivots(defaultGroups)
      },
      {
        title: 'Queue',
        field: 'queue_id',
        options: [{ value: 'all', label: 'All' }, ...queues]
      }
    ]

    const groupForGrouping = _.find(defaultGroups, ({ name }) => (name === group))
    const { ticketField, labels } = groupForGrouping
    const accordionData = _(tickets)
      .orderBy(ticketField)
      .groupBy((ticket) => {
        const value = ticket[ticketField]
        if (labels && labels[value]) { return labels[value] }

        return value
      })
      .value()

    return (
      <Card padding={false} allowOverflow>
        <ContentSectionBlock
          title="Filters"
          actions={[
            {
              onClick: (e) => { resetFilters(e) },
              text: 'Reset'
            }
          ]}
        >
          <Row classExtra="thin-gutters">
            {_.map(selectInputFilters, ({ title, field, options }) => (
              <Col key={field}>
                <FormInput label={title}>
                  <FormInputSelect
                    placeholder={`Select a ${title}`}
                    value={_.find(options, { value: filters[field] })}
                    options={_.map(options, ({ label, value }) => ({ label, value }))}
                    onValue={value => handleFilterInputChange(field, value)}
                    disabled={loadingTickets}
                  />
                </FormInput>
                {loadingTickets}
              </Col>
            ))}
            <Col>
              <FormInput label="Date Range">
                <FormInputDateRange
                  value={[dateFrom, dateTo]}
                  onValue={value => (handleDateRangeInputChange(value))}
                  disabled={loadingTickets}
                />
              </FormInput>
            </Col>
          </Row>
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <div><p>Tickets will be loaded once you filter them </p></div>
            <div>
              <Button disabled={loadingTickets} onClick={() => handleOnClickApplyFilters()}>Filter</Button>
            </div>
          </div>
        </ContentSectionBlock>
        <ContentSectionBlock>
          {loadingTickets ? (
            <Row>
              <Col width={10} />
              <Col width={3}>
                <Spinner fill />
              </Col>
              <Col width={11} />
            </Row>
          ) : <ReportAccordion data={accordionData} />
          }
        </ContentSectionBlock>
      </Card>
    )
  }
}
